import { Alert, BusinessSelector, Button, Empty, IBusinessOption, ModalV2 } from '@components'
import {
  fetchUsershipNotificationPreferences,
  IUsershipNotificationPreferences,
  NOTIFICATION_CATEGORY_KEYS,
  updateUsershipNotificationPreferences
} from '@query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { FaCheck } from 'react-icons/fa'
import styled from 'styled-components'
import {
  NotificationPreferencesForm,
  NotificationPreferencesLoading
} from './NotificationPreferencesForm'
import { useNotificationPrefsBusiness } from './useNotificationPrefsBusiness'

interface Props {
  isVisible: boolean
  onClose: () => void
}

const samePreferences = (
  a: IUsershipNotificationPreferences,
  b: IUsershipNotificationPreferences
): boolean =>
  a.accounting_suggestions_digest_frequency === b.accounting_suggestions_digest_frequency &&
  NOTIFICATION_CATEGORY_KEYS.every(
    key =>
      a.categories[key] === b.categories[key] && a.email_categories[key] === b.email_categories[key]
  )

export const NotificationPreferencesModal: React.FC<Props> = ({ isVisible, onClose }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    showSelector,
    hasBusinesses,
    selectedBusiness,
    slug,
    changeBusiness: setPickedBusiness,
    isResolvingSoleBusiness,
    soleBusinessResolveError,
    retrySoleBusinessResolve
  } = useNotificationPrefsBusiness(isVisible)

  const [edit, setEdit] = useState<IUsershipNotificationPreferences | null>(null)
  const [wasVisible, setWasVisible] = useState(isVisible)

  if (isVisible !== wasVisible) {
    setWasVisible(isVisible)
    if (isVisible) {
      setEdit(null)
    }
  }

  const queryKey = [slug, 'usership_notification_preferences'] as const

  const prefsQuery = useQuery({
    queryKey,
    queryFn: () => fetchUsershipNotificationPreferences({ slug }),
    enabled: Boolean(slug) && isVisible
  })

  const baseline = prefsQuery.data ?? null
  const draft = edit ?? baseline
  const isDirty = Boolean(edit && baseline && !samePreferences(edit, baseline))
  const showError = prefsQuery.isError && !prefsQuery.isFetching

  const savePrefs = useMutation({
    mutationFn: (payload: IUsershipNotificationPreferences) =>
      updateUsershipNotificationPreferences({ slug }, payload),
    onSuccess: data => {
      queryClient.setQueryData(queryKey, data)
      setEdit(null)
      toast.success(t('notificationFeed.preferences.preferencesSaved'))
      onClose()
    },
    onError: () => {
      toast.error(t('general.error'))
    }
  })

  const canSave = Boolean(draft) && isDirty && !savePrefs.isPending && !showError

  const changeBusiness = (business: IBusinessOption | null) => {
    setPickedBusiness(business)
    setEdit(null)
  }

  const renderBody = () => {
    if (showError) {
      return (
        <ErrorState>
          <Alert
            type="error"
            marginBottom={0}
            title={t('notificationFeed.preferences.loadErrorTitle')}
            description={t('notificationFeed.preferences.loadErrorDescription')}
          />
          <Button type="button" onClick={() => prefsQuery.refetch()}>
            {t('notificationFeed.preferences.tryAgain')}
          </Button>
        </ErrorState>
      )
    }

    if (!selectedBusiness) {
      if (isResolvingSoleBusiness) {
        return <NotificationPreferencesLoading />
      }
      if (soleBusinessResolveError) {
        return (
          <ErrorState>
            <Alert
              type="error"
              marginBottom={0}
              title={t('notificationFeed.preferences.loadErrorTitle')}
              description={t('notificationFeed.preferences.loadErrorDescription')}
            />
            <Button type="button" onClick={() => retrySoleBusinessResolve()}>
              {t('notificationFeed.preferences.tryAgain')}
            </Button>
          </ErrorState>
        )
      }
      if (!hasBusinesses) {
        return (
          <Alert
            type="info"
            marginBottom={0}
            description={
              <Trans
                i18nKey="notificationFeed.preferences.noBusinessesForPreferences"
                components={{ 0: <Link to="/onboarding" /> }}
              />
            }
          />
        )
      }
      if (!showSelector) {
        return <Empty />
      }
      return <Empty text={t('notificationFeed.preferences.selectBusinessPrompt')} />
    }

    return (
      <>
        <Intro>
          {t('notificationFeed.preferences.modalDescription', {
            businessName: selectedBusiness.name
          })}
        </Intro>
        {draft ? (
          <NotificationPreferencesForm value={draft} onChange={setEdit} />
        ) : (
          <NotificationPreferencesLoading />
        )}
      </>
    )
  }

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={onClose}
      header={t('notificationFeed.preferences.modalTitle')}
      width={520}
      height={650}
      footer={
        <>
          <Button type="button" onClick={onClose} disabled={savePrefs.isPending}>
            {t('general.cancel')}
          </Button>
          <Button
            type="button"
            intent="success"
            icon={<FaCheck />}
            onClick={() => {
              if (!draft || !canSave) {
                return
              }
              savePrefs.mutate(draft)
            }}
            disabled={!canSave}
            showSpinner={savePrefs.isPending}
          >
            {t('general.save')}
          </Button>
        </>
      }
    >
      {showSelector && (
        <SelectorWrapper>
          <BusinessSelector
            label={t('notificationFeed.preferences.businessLabel')}
            value={selectedBusiness}
            onChange={changeBusiness}
            disabled={savePrefs.isPending}
          />
        </SelectorWrapper>
      )}
      {renderBody()}
    </ModalV2>
  )
}

const SelectorWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;
`

const Intro = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.lg}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
